/*// Enum for sort directions
export type SortDirection = "asc" | "desc";

// Generic sort_by function for arrays of objects with flexible sort order
function sortBy<T>(
  ...fields: Array<{ key: keyof T; order: SortDirection }>
): (a: T, b: T) => number {
  return (a: T, b: T) => {
    for (const { key, order } of fields) {
      const aValue = a[key];
      const bValue = b[key];

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
    }
    return 0;
  };
}
export { sortBy };*/
type SortDirection = "asc" | "desc";

type SortField<T> = {
  key: string | ((item: T) => unknown);
  order: SortDirection;
};

function getByPath(obj: unknown, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = obj;

  for (const part of parts) {
    if (cur == null) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function compareUnknown(a: unknown, b: unknown): number {
  // Put null/undefined last (change if you want them first)
  const aNil = a == null;
  const bNil = b == null;
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;

  // numeric compare when both numbers
  if (typeof a === "number" && typeof b === "number") return a - b;

  // fallback: string compare
  return String(a).localeCompare(String(b));
}

/**
 * Builds a comparator function for `Array.prototype.sort()` with support for:
 * - multiple sort fields (primary, secondary, ...),
 * - ascending/descending per field,
 * - nested property access via dotted paths,
 * - custom accessor functions.
 *
 * ## Usage
 *
 * ### 1) Sort by a direct property (ascending)
 * ```ts
 * items.sort(sortBy({ key: "name", order: "asc" }));
 * ```
 *
 * ### 2) Sort by multiple fields (name, then iso)
 * ```ts
 * items.sort(
 *   sortBy(
 *     { key: "name", order: "asc" },
 *     { key: "iso", order: "asc" },
 *   ),
 * );
 * ```
 *
 * ### 3) Sort by a nested property using a dotted path
 * ```ts
 * items.sort(sortBy({ key: "licenseOption.name", order: "desc" }));
 * ```
 *
 * ### 4) Sort using an accessor function (useful for computed values / type-safety)
 * ```ts
 * items.sort(sortBy({ key: (x) => x.licenseOption?.name ?? "", order: "asc" }));
 * ```
 *
 * ## Behavior notes
 * - Comparison is stable *per field*: the first field that differs decides the order.
 * - `null`/`undefined` values are placed **last** (regardless of asc/desc), then remaining
 *   values are compared (numbers numerically, everything else as strings).
 *
 * @param fields One or more sort descriptors. Earlier descriptors have higher priority.
 * @returns A comparator compatible with `Array.prototype.sort()`.
 */
export function sortBy<T>(
  ...fields: Array<SortField<T>>
): (a: T, b: T) => number {
  return (a, b) => {
    for (const { key, order } of fields) {
      const aValue = typeof key === "function" ? key(a) : getByPath(a, key);
      const bValue = typeof key === "function" ? key(b) : getByPath(b, key);

      const cmp = compareUnknown(aValue, bValue);
      if (cmp !== 0) return order === "asc" ? cmp : -cmp;
    }
    return 0;
  };
}
